/**
 * chunkErrorGuard — rede de segurança para `ChunkLoadError`.
 *
 * Quando sai um deploy novo, os chunks antigos deixam de existir no CDN. Uma aba
 * que ficou aberta e tenta carregar um chunk sob demanda (`React.lazy` /
 * `import()`) recebe 404 e a tela quebra. Hoje não há rota lazy no app, mas o
 * flow-builder vai ter — então o guard entra antes, junto do aviso de versão.
 *
 * Ao detectar, recarrega a página UMA vez. O "uma vez" é essencial: se o deploy
 * estiver realmente quebrado, recarregar em loop é pior que a tela de erro.
 *
 * Duas travas:
 * - flag em memória (`reloadAttemptedThisPageLoad`) → uma TENTATIVA de reload por
 *   carregamento de página. É tentativa, não reload: a flag é queimada assim que
 *   um ChunkLoadError chega, mesmo que o cooldown acabe vetando o reload;
 * - carimbo em `sessionStorage` com cooldown → se o reload não resolveu (erro
 *   volta na sequência), não tenta de novo.
 *
 * Passado o cooldown, um ChunkLoadError legítimo volta a disparar reload — mas
 * só ENTRE carregamentos de página, nunca dentro do mesmo. Cenário de nicho que
 * isso implica: se um erro foi vetado pelo cooldown, a tentativa daquele page
 * load já foi consumida e um erro posterior no mesmo page load não recarrega —
 * o usuário precisa de um F5 manual. É escolha consciente: preferimos exigir o
 * F5 a arriscar reload em cadência de 60s numa aba com deploy quebrado.
 *
 * Como o escopo do carimbo é `sessionStorage`, outra aba/sessão começa limpa.
 */
const RELOAD_KEY = "nexus:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 60_000;

// Webpack não é consistente: às vezes o erro chega com `name === "ChunkLoadError"`,
// às vezes só a mensagem denuncia. Checamos os dois sinais.
const CHUNK_ERROR_RE =
  /ChunkLoadError|Loading chunk\s+\S+\s+failed|Loading CSS chunk\s+\S+\s+failed/i;

let reloadAttemptedThisPageLoad = false;

/** Exposto só para os testes: zera a trava em memória. */
export function resetChunkGuardState() {
  reloadAttemptedThisPageLoad = false;
}

export function isChunkLoadError(error, fallbackMessage) {
  if (error && error.name === "ChunkLoadError") return true;
  const message =
    (error && typeof error.message === "string" && error.message) ||
    (typeof error === "string" ? error : "") ||
    (typeof fallbackMessage === "string" ? fallbackMessage : "");
  return CHUNK_ERROR_RE.test(message);
}

function shouldReload() {
  if (reloadAttemptedThisPageLoad) return false;
  // Queima a tentativa ANTES do cooldown decidir, de propósito: mover esta linha
  // para dentro do `return true` faria cada erro dentro do cooldown reconsultar
  // o relógio e, ao expirar, recarregar em cadência de 60s. Ver o comentário do
  // topo do arquivo.
  reloadAttemptedThisPageLoad = true;

  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY)) || 0;
    if (last > 0 && Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage indisponível: a flag em memória ainda garante no máximo
    // uma tentativa de reload por carregamento de página.
  }

  return true;
}

function handleErrorEvent(event) {
  if (!isChunkLoadError(event && event.error, event && event.message)) return;
  if (!shouldReload()) return;
  window.location.reload();
}

function handleRejectionEvent(event) {
  // `import()` que falha rejeita uma Promise — não gera `error` event.
  if (!isChunkLoadError(event && event.reason)) return;
  if (!shouldReload()) return;
  window.location.reload();
}

/**
 * Instala os listeners globais. Chamar uma vez, no bootstrap (src/index.js).
 * Retorna uma função de desinstalação (usada nos testes).
 */
export function installChunkErrorGuard() {
  window.addEventListener("error", handleErrorEvent);
  window.addEventListener("unhandledrejection", handleRejectionEvent);

  return function uninstallChunkErrorGuard() {
    window.removeEventListener("error", handleErrorEvent);
    window.removeEventListener("unhandledrejection", handleRejectionEvent);
  };
}

export default installChunkErrorGuard;
